import { compact, isEmpty } from 'lodash';
import useSWRImmutable from 'swr/immutable';

import { MODE_WATER } from '@/lib/constants';
import { LocationModel, MinimalLocationModel } from '@/lib/models/location/types';
import { MapMarker, Paths, PositionInterface } from '@/types';
import { RoutePolylineApiInterface, SegmentModeType } from '@/types/api-types';
import { AsyncDataInterface } from '@/types/context-types';
import { request } from '@/utils/api/request';
import { generateSimpleHash } from '@/utils/helpers';
import { getPathsApiUrl } from '@/utils/helpers/api-urls';
import { createPathId, createPathsFromLocations, decodePolylinePath } from '@/utils/mapping';

const ERROR_MESSAGE = 'Error fetching Paths';

const getPathIdByIndex = (nodeIds: string[], index: number, hash: string | number) =>
  createPathId(nodeIds[index], nodeIds[index + 1], hash);

// Adds the Start and end Links to the Path so that we can ensure the lines go into the Ports and don't end short
const addStartEndLinksToPath = (
  positions: PositionInterface[],
  start?: LocationModel | MapMarker | MinimalLocationModel,
  end?: LocationModel | MapMarker | MinimalLocationModel,
): PositionInterface[] => compact([start?.location, ...positions, end?.location]);

interface PathsState extends AsyncDataInterface {
  paths: Paths[];
}

export const usePaths = (
  locations: LocationModel[] | MapMarker[] | MinimalLocationModel[],
  type: SegmentModeType = MODE_WATER,
  includeStartEndlinks = false,
): PathsState => {
  const locationIds = locations.map((location) => location.id);
  const pathHash = generateSimpleHash(locationIds.join('')); // used to ensure paths have unique keys to avoid collisions in Gmaps
  const shouldFetchRemotePaths = !isEmpty(locationIds) && type === MODE_WATER;

  const { data, isLoading, error } = useSWRImmutable<{ pathCoords: RoutePolylineApiInterface[] }>(
    shouldFetchRemotePaths ? getPathsApiUrl(locationIds) : '',
    request,
  );

  const errorMessage = error ? ERROR_MESSAGE : undefined;

  // If paths are received, decode and return
  // Else build paths using the locations store provided
  const decodedPaths: Paths[] = data?.pathCoords
    ? data.pathCoords.map((coords, index) => {
        const fromLocation = locations[index];
        const toLocation = locations[index + 1];
        const decodedPositions = decodePolylinePath(coords.encodedPath) || [];

        return {
          id: getPathIdByIndex(locationIds, index, pathHash),
          fromId: fromLocation.id,
          toId: toLocation?.id,
          positions: includeStartEndlinks
            ? addStartEndLinksToPath(decodedPositions, fromLocation, toLocation)
            : decodedPositions,
        };
      })
    : createPathsFromLocations(locations, pathHash);

  return {
    isLoading,
    error: errorMessage,
    paths: decodedPaths,
  };
};
